import React, { useState, useEffect, useCallback } from "react";
import type { Toast, ToastEventDetail, ToastType } from "../../types/toast";

interface ToastContainerProps {
  [key: string]: string | undefined;
}

const ToastContainer: React.FC<ToastContainerProps> = (props) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Convert flash messages to toasts on mount
  useEffect(() => {
    const flashToasts: Toast[] = [];
    Object.entries(props).forEach(([key, message]) => {
      if (message) {
        const type = getToastType(key);
        const id = `flash-${key}-${Date.now()}`;
        flashToasts.push({ id, message, type, duration: 6000 });
      }
    });
    if (flashToasts.length > 0) {
      setToasts(flashToasts);
    }
  }, [props]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    const handleToastAdd = (event: CustomEvent<ToastEventDetail>) => {
      const { message, type, duration = 6000 } = event.detail;
      const id = `toast-${Date.now()}-${Math.random()}`;

      const newToast: Toast = { id, message, type, duration };
      setToasts((prev) => [...prev, newToast]);

      setTimeout(() => {
        removeToast(id);
      }, duration);
    };

    window.addEventListener("toast:add", handleToastAdd);
    return () => window.removeEventListener("toast:add", handleToastAdd);
  }, [removeToast]);

  const getToastType = (flashKey: string): ToastType => {
    switch (flashKey) {
      case "notice":
      case "success":
        return "success";
      case "alert":
      case "error":
        return "error";
      case "warning":
        return "warning";
      default:
        return "info";
    }
  };

  const getAlertClass = (type: Toast["type"]) => {
    switch (type) {
      case "success":
        return "alert-success";
      case "error":
        return "alert-error";
      case "warning":
        return "alert-warning";
      case "info":
      default:
        return "alert-info";
    }
  };

  return (
    <div className="toast toast-top toast-center z-50">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="alert"
          className={`alert ${getAlertClass(
            toast.type
          )} shadow-lg transition-all duration-300 ease-in-out animate-in fade-in slide-in-from-top-2`}
          data-testid="toast-message"
        >
          <span>{toast.message}</span>
          <button
            onClick={() => removeToast(toast.id)}
            className="btn btn-sm btn-circle btn-ghost"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
